/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import ReactECharts from "echarts-for-react";
import { useFetchData } from "../../../customHooks/useGetDashboardData";
import { COLORS } from "../../../utils/constants";
import CardLayout from "../../Cards/CardLayout";
import CardError from "../../feedBacks/CardError";
import Loader from "../../feedBacks/loader";
import NoContent from "../../feedBacks/NoContent";
import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IHierarchicalNode } from "../../../utils/interfaces";
import { useUtils } from "../../../customHooks/useUtils";

function OperationSites({ company_id, isFromFinance }: { company_id: string, isFromFinance?: boolean }) {
  const { FEEDBACK_MESSAGES, t } = useUtils();
  const chartRef = useRef<ReactECharts>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [chartReady, setChartReady] = useState(false);
  const navigate = useNavigate();
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chartInstanceRef = useRef<any>(null);

  const { data, isLoading, error } = useFetchData(
    ["/classifications", "fetchAssetClassifications", company_id],
    "/classifications/",
    {
      company_id,
    },
    // @ts-ignore
    {}
  );

  // Function to transform the API data into ECharts compatible format
  const transformToEChartsFormat = (data: IHierarchicalNode[]): any => {
    const transform = (node: IHierarchicalNode): any => {
      // Create the node itself
      const result: any = {
        name: node.name,
        id: node.id,
        itemStyle: {
          color: COLORS.BLACK_LIGHTER,
        },
      };

      const children: any[] = [];

      // Add regular children nodes
      if (node.children && node.children.length > 0) {
        children.push(...node.children.map(transform));
      }

      // Add device nodes as children if any exist
      if (node.devices && node.devices.length > 0) {
        node.devices.forEach((device) => {
          children.push({
            name: device.asset_name,
            id: device.id,
            value: device.asset_capacity,
            itemStyle: {
              color: COLORS.PRIMARY,
            },
            // Include device details for the tooltip
            deviceInfo: {
              type: device.asset_type,
              capacity: device.asset_capacity,
              year: device.transformer_year,
              nature: device.nature_of_transformer,
              gateway: device.gateway_serial,
              active: device.is_active,
            },
          });
        });

        // Highlight the parent node if it has devices
        result.itemStyle.color = COLORS.PRIMARY;
      }

      if (children.length > 0) {
        result.children = children;
      }

      return result;
    };

    return data?.map(transform) || [];
  };

  // Use transformed data for the chart - wrapped in useMemo to avoid dependency changes on every render
  const chartData = useMemo(() => {
    return data ? transformToEChartsFormat(data as IHierarchicalNode[]) : [];
  }, [data]);
  const getAssetType = useCallback(
    (type: string) => {
      switch (type) {
        case "transformer":
          return t("asset_type.transformer");
        case "generator":
          return t("asset_type.generator");
        case "gateway":
          return t("asset_type.gateway");
        default:
          return t("asset_type.unknown");
      }
    },
    [t]
  );
  // Create and configure the chart options
  const options = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return null;
    }

    return {
      tooltip: {
        trigger: "item",
        triggerOn: "mousemove",
        alwaysShowContent: false,
        enterable: true,
        formatter: function (params: any) {
          const { name, data } = params;

          // Enhanced tooltip to show device information when available
          if (data && data.deviceInfo) {
            // This is a device node
            const { type, capacity, year, nature, gateway, active } =
              data.deviceInfo;
            const assetId = data.id;
            return `
              <div style="padding: 5px">
                <div><strong>${t(
                  `site_details.asset_name`
                )}</strong> (${getAssetType(type)})</div>
                <div>${t(`site_details.asset_capacity`)}: ${capacity}</div>
                <div>${t(`site_details.year`)}: ${year}</div>
                <div>${t(`site_details.nature_of_ownership`)}: ${nature}</div>
                <div>${t(`site_details.gateway_serial`)}: ${gateway}</div>
                <div>${t(`common.status`)}: ${
              active ? t("common.active") : t("common.inactive")
            }</div>
                <div style="margin-top: 8px;">
                  <a href="/dashboard/${isFromFinance ? 'finance' : 'operations'}/site/${assetId}" data-asset-id="${assetId}" class="dashboard-link" style="color: #F59E0B; font-weight: 500; cursor: pointer; text-decoration: underline; display: flex; align-items: center;">
                    ${t(
                      "view_dashboard"
                    )} <span style="margin-left: 4px;">&#8599;</span>
                  </a>
                </div>
              </div>
            `;
          } else {
            // This is a layer node
            const hasDevices =
              data.itemStyle && data.itemStyle.color === COLORS.PRIMARY;
            const layerId = data.id;

            let dashboardLink = "";
            if (hasDevices && !isFromFinance) {
              dashboardLink = `
                <div style="margin-top: 8px;">
                  <a href="/dashboard/operations/sites/aggregate/${layerId}" data-layer-id="${layerId}" class="dashboard-link" style="color: #F59E0B; font-weight: 500; cursor: pointer; text-decoration: underline; display: flex; align-items: center;">
                    ${t(
                      "view_aggregate_dashboard"
                    )} <span style="margin-left: 4px;">&#8599;</span>
                  </a>
                </div>
              `;
            }

            return `
              <div style="padding: 5px">
                <div><strong>${name}</strong></div>
                ${dashboardLink}
              </div>
            `;
          }
        },
      },
      series: [
        {
          type: "tree",
          name: "Network Hierarchy",
          data: chartData,
          top: "10%",
          left: "8%",
          bottom: "22%",
          right: "20%",
          symbolSize: 10,
          orient: "vertical",
          edgeShape: "polyline",
          edgeForkPosition: "63%",
          initialTreeDepth: 3,
          lineStyle: {
            width: 2,
          },
          label: {
            backgroundColor: "#fff",
            position: "top",
            verticalAlign: "middle",
            align: "center",
            fontSize: 12,
            distance: 5,
          },
          leaves: {
            label: {
              position: "right",
              verticalAlign: "middle",
              align: "left",
            },
          },
          emphasis: {
            focus: "descendant",
          },
          expandAndCollapse: true,
          animationDuration: 200,
          animationDurationUpdate: 300,
          roam: true, // Enable panning and zooming
        },
      ],
    };
  }, [chartData, t, getAssetType, isFromFinance]);

  // Initialize chart when component mounts
  useEffect(() => {
    // Function to perform chart initialization
    const initializeChart = () => {
      if (!chartRef.current) return;

      const chartInstance = chartRef.current.getEchartsInstance();
      if (!chartInstance) return;

      chartInstanceRef.current = chartInstance;

      // Wait a bit to ensure DOM is ready
      setTimeout(() => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.resize();
          setChartReady(true);
        }
      }, 100);
    };

    if (chartRef.current && !isLoading && chartData.length > 0) {
      initializeChart();
    }

    // Add window resize handler
    const handleResize = () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isLoading, chartData]);

  // Handle click on node to toggle fixed tooltip and handle dashboard navigation
  useEffect(() => {
    if (!chartReady || !chartRef.current) return;

    const chartInstance = chartInstanceRef.current;
    if (!chartInstance) return;

    // Clear previous event listeners to prevent duplicates
    chartInstance.off("click");
    chartInstance.off("treeexpand");
    chartInstance.off("treecollapse");

    // Add click event listener with debounce to avoid double-triggering
    chartInstance.on("click", (params: any) => {
      // Clear any pending click to avoid double processing
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);

      // Small delay to avoid race conditions in production
      clickTimeoutRef.current = setTimeout(() => {
        if (!params || !params.data) return;

        const nodeId = params.data.id;

        // Toggle selected node
        if (selectedNodeId === nodeId) {
          setSelectedNodeId(null);
          chartInstance.dispatchAction({
            type: "hideTip",
          });
        } else {
          setSelectedNodeId(nodeId);

          // Use the params directly from the click event
          chartInstance.dispatchAction({
            type: "showTip",
            seriesIndex: params.seriesIndex,
            dataIndex: params.dataIndex,
          });
        }
      }, 50);
    });

    // Handle node expanding
    chartInstance.on("treeexpand", () => {
      // Force layout recalculation after expansion
      setTimeout(() => {
        chartInstance.resize();
      }, 300);
    });

    // Handle node collapsing
    chartInstance.on("treecollapse", () => {
      // Force layout recalculation after collapse
      setTimeout(() => {
        chartInstance.resize();
      }, 300);
    });

    // Handle clicking on the dashboard link inside tooltip
    const handleTooltipLinkClick = (event: MouseEvent) => {
      // Check if the click was on a link inside the tooltip
      const target = event.target as HTMLElement;
      const link = target.closest(".dashboard-link") as HTMLAnchorElement;

      if (link && link.closest(".echarts-tooltip")) {
        event.preventDefault();

        // Check if it's an asset or layer link
        const assetId = link.getAttribute("data-asset-id");
        const layerId = link.getAttribute("data-layer-id");

        if (assetId) {
          navigate(`/dashboard/operations/site/${assetId}`);
        } else if (layerId) {
          navigate(`/dashboard/operations/sites/aggregate/${layerId}`);
        }
      }
    };

    document.addEventListener("click", handleTooltipLinkClick);

    return () => {
      // Clean up all event listeners
      document.removeEventListener("click", handleTooltipLinkClick);
      if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
      if (chartInstance) {
        chartInstance.off("click");
        chartInstance.off("treeexpand");
        chartInstance.off("treecollapse");
      }
    };
  }, [chartReady, selectedNodeId, navigate]);

  // Persist tooltip for selected node and handle node expansion
  useEffect(() => {
    if (!selectedNodeId || !chartReady || !chartInstanceRef.current) return;

    const chart = chartInstanceRef.current;

    // Get all nodes data with a more robust flattening algorithm
    const flattenNodes = (nodes: any[]): any[] => {
      if (!nodes || !Array.isArray(nodes)) return [];

      let result: any[] = [];

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (node) {
          result.push(node);
          if (node.children && Array.isArray(node.children)) {
            result = result.concat(flattenNodes(node.children));
          }
        }
      }

      return result;
    };

    const attemptToSelectNode = () => {
      try {
        const allNodes = flattenNodes(chartData);

        // Find the selected node
        const selectedNode = allNodes.find(
          (node) => node && node.id === selectedNodeId
        );

        if (selectedNode) {
          // Approach 1: Use name to find the node (more reliable in most cases)
          chart.dispatchAction({
            type: "highlight",
            name: selectedNode.name,
          });

          chart.dispatchAction({
            type: "showTip",
            name: selectedNode.name,
          });

          // Approach 2: As a fallback, try to expand all parent nodes to ensure visibility
          if (selectedNode.parentId) {
            const parentNode = allNodes.find(
              (node) => node && node.id === selectedNode.parentId
            );
            if (parentNode) {
              chart.dispatchAction({
                type: "treeExpandTo",
                name: parentNode.name,
              });
            }
          }
        }
      } catch (err) {
        console.error("Error selecting node:", err);
      }
    };

    // Try once with a delay to handle production timing issues
    setTimeout(attemptToSelectNode, 300);
  }, [chartData, selectedNodeId, chartReady]);

  // Handle chart updates when data changes
  useEffect(() => {
    if (chartReady && chartInstanceRef.current && options) {
      chartInstanceRef.current.setOption(options, true);
    }
  }, [options, chartReady]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-full w-full">
          <Loader />
        </div>
      );
    } else if (error) {
      return (
        <CardError message={FEEDBACK_MESSAGES.ERROR} className="!h-[100px]" />
      );
    } else if (!data || !chartData || chartData.length === 0) {
      return <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />;
    } else {
      return (
        <div className="w-full h-[600px]">
          <ReactECharts
            ref={chartRef}
            option={options || { series: [] }}
            style={{ height: "100%", width: "100%" }}
            notMerge={false} // Changed to false to prevent flashing
            lazyUpdate={true} // Changed to true for better performance
            opts={{
              renderer: "canvas",
              devicePixelRatio: window.devicePixelRatio || 1,
            }}
            onChartReady={() => {
              setChartReady(true);
              if (chartRef.current) {
                chartInstanceRef.current =
                  chartRef.current.getEchartsInstance();
                // Ensure proper initial sizing
                setTimeout(() => {
                  if (chartInstanceRef.current) {
                    chartInstanceRef.current.resize();
                  }
                }, 50);
              }
            }}
          />
        </div>
      );
    }
  };

  return (
    <CardLayout title="" className="lg:min-w-[500px] !min-w-[300px]">
      <div className="flex flex-col h-full w-full relative">
        {chartData && chartData.length > 0 && (
          <div className="absolute top-[-5%] right-0 flex gap-2 justify-between z-10">
            <div className="flex-1 flex gap-2 justify-end">
              <div className="flex items-center gap-1 rounded-full bg-primary-blackLighter2 px-3 py-2">
                <span
                  className="w-4 h-4 rounded-md"
                  style={{ backgroundColor: COLORS.PRIMARY }}
                ></span>
                <span className="text-[11px]">{t("with_device")}</span>
              </div>
              <div className="flex items-center gap-1 rounded-full bg-primary-blackLighter2 px-3 py-2">
                <span
                  className="w-4 h-4 rounded-md"
                  style={{ backgroundColor: COLORS.BLACK_LIGHTER }}
                ></span>
                <span className="text-[11px]">{t("no_device")}</span>
              </div>
            </div>
          </div>
        )}
        {renderBody()}
      </div>
    </CardLayout>
  );
}

export default OperationSites;
